/**
 * Stinner — split combo selector (Suspension + Wheelset) for 3-option products.
 *
 * Gated in Liquid: snippets/pdp-split-combo-selector.liquid (product metafield + optional template suffix).
 * If validation fails, logs a console warning and leaves native variant UI untouched.
 */
(function () {
  'use strict';

  var WARN_PREFIX = '[Stinner split-combo]';
  var splitComboStateByConfig = new WeakMap();
  var variantChangeListenerAttached = false;

  function attachGlobalVariantChangeListener() {
    if (variantChangeListenerAttached) return;
    variantChangeListenerAttached = true;
    document.addEventListener('variant:change', function (ev) {
      if (!ev.detail || !ev.detail.variant) return;
      document
        .querySelectorAll('[data-stinner-split-combo-config][data-stinner-split-combo-initialized="true"]')
        .forEach(function (cfgEl) {
          var st = splitComboStateByConfig.get(cfgEl);
          if (!st || st.isApplyingNative) return;
          var sel = st.section.querySelector('[data-product-select]');
          if (!sel || parseInt(sel.value, 10) !== ev.detail.variant.id) return;
          var row = st.variants.filter(function (x) {
            return x.id === ev.detail.variant.id;
          })[0];
          st.syncCustomFromMasterVariant(row);
        });
    });
  }

  function norm(s) {
    if (s == null || s === undefined) return '';
    return String(s).trim().toLowerCase();
  }

  function otherOptionKeys(comboIdx) {
    return ['option1', 'option2', 'option3'].filter(function (_k, i) {
      return i !== comboIdx;
    });
  }

  function signatureForVariant(v, others, suspensionKey, wheelKey) {
    return (
      norm(v[others[0]]) +
      '|' +
      norm(v[others[1]]) +
      '|' +
      norm(v[suspensionKey]) +
      '|' +
      norm(v[wheelKey])
    );
  }

  function validateDataset(variants, comboIdx, suspensionKey, wheelKey) {
    if (!variants || !variants.length) {
      return { ok: false, reason: 'No variants' };
    }
    var others = otherOptionKeys(comboIdx);
    var map = {};
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (norm(v[suspensionKey]) === '' || norm(v[wheelKey]) === '') {
        return { ok: false, reason: 'Variant ' + v.id + ' missing suspension or wheel metafield value' };
      }
      var sig = signatureForVariant(v, others, suspensionKey, wheelKey);
      if (map[sig] != null) {
        return {
          ok: false,
          reason: 'Duplicate configuration maps to variants ' + map[sig] + ' and ' + v.id,
        };
      }
      map[sig] = v.id;
    }
    if (Object.keys(map).length !== variants.length) {
      return { ok: false, reason: 'Signature map size mismatch' };
    }
    return { ok: true, map: map, others: others };
  }

  function getOptionValue(section, optionKey) {
    var sel = section.querySelector('select[data-variant-input][data-index="' + optionKey + '"]');
    if (sel) return sel.value;
    var checked = section.querySelector(
      'input[type="radio"][data-variant-input][data-index="' + optionKey + '"]:checked'
    );
    return checked ? checked.value : null;
  }

  function setOptionValue(section, optionKey, value) {
    var sel = section.querySelector('select[data-variant-input][data-index="' + optionKey + '"]');
    if (sel) {
      sel.value = value;
      return sel;
    }
    var radios = section.querySelectorAll(
      'input[type="radio"][data-variant-input][data-index="' + optionKey + '"]'
    );
    var trigger = null;
    for (var i = 0; i < radios.length; i++) {
      var r = radios[i];
      r.checked = r.value === value;
      if (r.value === value) trigger = r;
    }
    return trigger;
  }

  function comboOptionKey(comboIdx) {
    return 'option' + (comboIdx + 1);
  }

  function findNativeComboWrapper(section, comboKey) {
    var inner = section.querySelector('[data-index="' + comboKey + '"]');
    return inner ? inner.closest('.variant-wrapper') : null;
  }

  function uniqueByNorm(values) {
    var seen = {};
    var out = [];
    for (var i = 0; i < values.length; i++) {
      var x = values[i];
      var k = norm(x);
      if (k === '') continue;
      if (!seen[k]) {
        seen[k] = true;
        out.push(x);
      }
    }
    out.sort(function (a, b) {
      return norm(a).localeCompare(norm(b));
    });
    return out;
  }

  function filterByOptions12(variants, others, o1, o2) {
    return variants.filter(function (v) {
      return norm(v[others[0]]) === norm(o1) && norm(v[others[1]]) === norm(o2);
    });
  }

  function filterBySuspension(subset, suspensionKey, sus) {
    return subset.filter(function (v) {
      return norm(v[suspensionKey]) === norm(sus);
    });
  }

  function resolveVariant(variants, others, suspensionKey, wheelKey, o1, o2, sus, whl) {
    var matches = variants.filter(function (v) {
      return (
        norm(v[others[0]]) === norm(o1) &&
        norm(v[others[1]]) === norm(o2) &&
        norm(v[suspensionKey]) === norm(sus) &&
        norm(v[wheelKey]) === norm(whl)
      );
    });
    if (matches.length === 1) return matches[0];
    if (matches.length > 1) {
      console.warn(
        WARN_PREFIX,
        'Multiple variants match this selection; treating as unavailable (configuration error).'
      );
    }
    return null;
  }

  function dispatchUnavailable(section) {
    var detail = {
      variant: null,
      currentlySelectedValues: [],
      value: null,
      index: null,
    };
    section.dispatchEvent(new CustomEvent('variantChange', { detail: detail }));
    document.dispatchEvent(new CustomEvent('variant:change', { detail: detail }));
  }

  /**
   * Master <select name="id"> often has no value when every variant is sold out (options are disabled with no value).
   * Fall back to ?variant=, theme's current-variant JSON, then first row in our config.
   */
  function resolveInitialVariantId(section, variants) {
    var master = section.querySelector('[data-product-select]');
    if (master && master.value) {
      var id = parseInt(master.value, 10);
      if (!isNaN(id)) return id;
    }
    try {
      var params = new URLSearchParams(window.location.search);
      var qv = params.get('variant');
      if (qv) {
        var fromUrl = parseInt(qv, 10);
        if (!isNaN(fromUrl)) return fromUrl;
      }
    } catch (e) {}
    var cv = section.querySelector('[data-current-variant-json]');
    if (cv && cv.textContent.trim()) {
      try {
        var obj = JSON.parse(cv.textContent);
        if (obj && obj.id != null) return obj.id;
      } catch (e) {}
    }
    if (variants.length) return variants[0].id;
    return NaN;
  }

  function flexCountForValues(len) {
    if (len <= 0) return 1;
    return len > 3 ? 4 : len;
  }

  /**
   * Impulse theme.Variants._onSelectChange destructures { srcElement } from the event.
   * Synthetic Event() objects often omit srcElement; without it the handler throws before updating the master <select>.
   */
  function dispatchVariantInputChange(el) {
    if (!el) return;
    var ev = new Event('change', { bubbles: true });
    try {
      Object.defineProperty(ev, 'srcElement', { value: el, enumerable: true });
    } catch (e) {}
    el.dispatchEvent(ev);
  }

  /** Keep master <select name="id"> in sync — theme.utils.serialize(form) posts this for /cart/add.js */
  function syncMasterVariantSelect(section, v) {
    var master = section.querySelector('[data-product-select]');
    if (!master || !v) return;
    var vid = String(v.id);
    master.value = vid;
    if (master.value !== vid) {
      console.warn(
        WARN_PREFIX,
        'Master variant <select> has no <option> for id',
        vid,
        '(re-publish or refresh after inventory changes)'
      );
      return;
    }
    master.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** Impulse-style button radios (same pattern as snippets/variant-button.liquid). Not data-variant-input — theme must ignore these. */
  function fillButtonGroup(fieldset, values, preferred, opts) {
    var productId = opts.productId;
    var radioName = opts.radioName;
    var legendText = opts.legendText;
    var fc = flexCountForValues(values.length);
    fieldset.className = 'variant-input-wrap flex-count-' + fc;
    fieldset.innerHTML = '';
    var leg = document.createElement('legend');
    leg.className = 'hide';
    leg.textContent = legendText;
    fieldset.appendChild(leg);
    var prevNorm = preferred != null && preferred !== '' ? norm(preferred) : '';
    var checkedAny = false;
    for (var i = 0; i < values.length; i++) {
      var val = values[i];
      var vid = 'stinner-' + radioName + '-' + productId + '-' + i;
      var wrap = document.createElement('div');
      wrap.className = 'variant-input variant-count-' + fc;
      var inp = document.createElement('input');
      inp.type = 'radio';
      inp.name = 'stinner-split-' + radioName + '-' + productId;
      inp.value = val;
      inp.id = vid;
      if (prevNorm && norm(val) === prevNorm) {
        inp.checked = true;
        checkedAny = true;
      }
      var lab = document.createElement('label');
      lab.htmlFor = vid;
      lab.className = 'variant__button-label variant-width-' + fc;
      lab.textContent = val;
      wrap.appendChild(inp);
      wrap.appendChild(lab);
      fieldset.appendChild(wrap);
    }
    if (!checkedAny && values.length) {
      var first = fieldset.querySelector('input[type="radio"]');
      if (first) first.checked = true;
    }
  }

  function getRadioGroupValue(fieldset) {
    var r = fieldset.querySelector('input[type="radio"]:checked');
    return r ? r.value : '';
  }

  function initFromConfigEl(cfgEl) {
    if (cfgEl.getAttribute('data-stinner-split-combo-initialized') === 'true') return;
    var section = cfgEl.closest('[data-section-type="product"]');
    if (!section) return;

    var cfg;
    try {
      cfg = JSON.parse(cfgEl.textContent || '{}');
    } catch (e) {
      console.warn(WARN_PREFIX, 'Invalid config JSON', e);
      return;
    }

    var variants = cfg.variants;
    var comboIdx = parseInt(cfg.comboOptionIndex, 10);
    if (isNaN(comboIdx) || comboIdx < 0 || comboIdx > 2) {
      console.warn(WARN_PREFIX, 'Invalid comboOptionIndex');
      return;
    }

    var comboKey = comboOptionKey(comboIdx);
    var suspensionKey = 'suspension';
    var wheelKey = 'wheel';

    var validation = validateDataset(variants, comboIdx, suspensionKey, wheelKey);
    if (!validation.ok) {
      console.warn(WARN_PREFIX, 'Validation failed — native variant UI left as-is.', validation.reason);
      return;
    }
    var others = validation.others;

    var master = section.querySelector('[data-product-select]');
    if (!master) {
      console.warn(WARN_PREFIX, 'No master variant select');
      return;
    }

    var initialId = resolveInitialVariantId(section, variants);
    var initial = variants.filter(function (v) {
      return v.id === initialId;
    })[0];
    if (!initial) {
      console.warn(WARN_PREFIX, 'Could not resolve initial variant id', initialId);
      return;
    }

    var nativeWrap = findNativeComboWrapper(section, comboKey);
    if (!nativeWrap) {
      console.warn(WARN_PREFIX, 'Could not find native combo option wrapper');
      return;
    }

    attachGlobalVariantChangeListener();

    nativeWrap.classList.add('stinner-split-combo-native-combo', 'is-stinner-split-combo-hidden');

    /** Same layout as variant-button.liquid (pdp-carrizo-variant-label) when bike-series PDP is active */
    var useCarrizoLabels = section.classList.contains('product-section--pdp-bike-series');
    var susForId = 'stinner-split-suspension-fs-' + cfg.productId;
    var wheelForId = 'stinner-split-wheel-fs-' + cfg.productId;

    function buildSplitComboLabel(forId, titleUpper, dataKey) {
      var lab = document.createElement('label');
      lab.setAttribute('for', forId);
      if (useCarrizoLabels) {
        lab.className = 'variant__label pdp-carrizo-variant-label';
        lab.innerHTML =
          '<span class="pdp-carrizo-opt-head">' +
          titleUpper.toUpperCase() +
          '</span>' +
          '<span class="variant__label-info pdp-carrizo-opt-val">' +
          '<span class="pdp-carrizo-opt-slash" aria-hidden="true">/</span>' +
          '<span data-stinner-split-label="' +
          dataKey +
          '"></span>' +
          '</span>';
      } else {
        lab.className = 'variant__label';
        lab.innerHTML =
          titleUpper +
          '<span class="variant__label-info"> &mdash; <span data-stinner-split-label="' +
          dataKey +
          '"></span></span>';
      }
      return lab;
    }

    var ui = document.createElement('div');
    ui.className = 'stinner-split-combo-custom';

    var susRow = document.createElement('div');
    susRow.className = 'variant-wrapper js';
    susRow.setAttribute('data-type', 'button');
    var susLabel = buildSplitComboLabel(susForId, 'Suspension', 'suspension');
    var susFieldset = document.createElement('fieldset');
    susFieldset.id = susForId;
    susFieldset.setAttribute('data-stinner-split-suspension', '');
    susRow.appendChild(susLabel);
    susRow.appendChild(susFieldset);

    var wheelRow = document.createElement('div');
    wheelRow.className = 'variant-wrapper js';
    wheelRow.setAttribute('data-type', 'button');
    var wheelLabel = buildSplitComboLabel(wheelForId, 'Wheelset', 'wheelset');
    var wheelFieldset = document.createElement('fieldset');
    wheelFieldset.id = wheelForId;
    wheelFieldset.setAttribute('data-stinner-split-wheelset', '');
    wheelRow.appendChild(wheelLabel);
    wheelRow.appendChild(wheelFieldset);

    var susValueSpan = susLabel.querySelector('[data-stinner-split-label="suspension"]');
    var wheelValueSpan = wheelLabel.querySelector('[data-stinner-split-label="wheelset"]');

    ui.appendChild(susRow);
    ui.appendChild(wheelRow);
    nativeWrap.insertAdjacentElement('afterend', ui);

    var btnOptsBase = {
      productId: cfg.productId,
    };
    var state = { isApplyingNative: false, section: section, variants: variants, syncCustomFromMasterVariant: null };
    splitComboStateByConfig.set(cfgEl, state);

    function updateSplitComboLabels(suspensionText, wheelText) {
      var sus =
        suspensionText != null && suspensionText !== ''
          ? String(suspensionText)
          : getRadioGroupValue(susFieldset);
      var wh =
        wheelText != null && wheelText !== '' ? String(wheelText) : getRadioGroupValue(wheelFieldset);
      if (susValueSpan) {
        susValueSpan.textContent = useCarrizoLabels ? sus.toUpperCase() : sus;
      }
      if (wheelValueSpan) {
        wheelValueSpan.textContent = useCarrizoLabels ? wh.toUpperCase() : wh;
      }
    }

    function refreshSuspensionOptions(preferredSus) {
      var o1 = getOptionValue(section, others[0]);
      var o2 = getOptionValue(section, others[1]);
      var pool = filterByOptions12(variants, others, o1, o2);
      var list = uniqueByNorm(pool.map(function (v) {
        return v[suspensionKey];
      }));
      fillButtonGroup(susFieldset, list, preferredSus, Object.assign({}, btnOptsBase, {
        radioName: 'suspension',
        legendText: 'Suspension',
      }));
      updateSplitComboLabels();
    }

    function refreshWheelOptions(preferredWheel) {
      var o1 = getOptionValue(section, others[0]);
      var o2 = getOptionValue(section, others[1]);
      var pool = filterByOptions12(variants, others, o1, o2);
      pool = filterBySuspension(pool, suspensionKey, getRadioGroupValue(susFieldset));
      var list = uniqueByNorm(pool.map(function (v) {
        return v[wheelKey];
      }));
      fillButtonGroup(wheelFieldset, list, preferredWheel, Object.assign({}, btnOptsBase, {
        radioName: 'wheel',
        legendText: 'Wheelset',
      }));
      updateSplitComboLabels();
    }

    function applyResolvedVariant(v) {
      if (!v) {
        dispatchUnavailable(section);
        return;
      }
      state.isApplyingNative = true;
      setOptionValue(section, others[0], v[others[0]]);
      setOptionValue(section, others[1], v[others[1]]);
      var trigger = setOptionValue(section, comboKey, v[comboKey]);
      var el = trigger || section.querySelector('[data-variant-input][data-index="' + comboKey + '"]');
      if (el) {
        dispatchVariantInputChange(el);
      }
      syncMasterVariantSelect(section, v);
      updateSplitComboLabels(v[suspensionKey], v[wheelKey]);
      requestAnimationFrame(function () {
        syncMasterVariantSelect(section, v);
        updateSplitComboLabels(v[suspensionKey], v[wheelKey]);
        state.isApplyingNative = false;
      });
    }

    function tryResolveFromCustom() {
      var o1 = getOptionValue(section, others[0]);
      var o2 = getOptionValue(section, others[1]);
      var sus = getRadioGroupValue(susFieldset);
      var wh = getRadioGroupValue(wheelFieldset);
      var v = resolveVariant(variants, others, suspensionKey, wheelKey, o1, o2, sus, wh);
      if (v) {
        applyResolvedVariant(v);
      } else {
        dispatchUnavailable(section);
      }
    }

    function syncCustomFromMasterVariant(variantRow) {
      if (!variantRow) return;
      refreshSuspensionOptions(variantRow[suspensionKey]);
      refreshWheelOptions(variantRow[wheelKey]);
    }

    state.syncCustomFromMasterVariant = syncCustomFromMasterVariant;

    refreshSuspensionOptions(initial[suspensionKey]);
    refreshWheelOptions(initial[wheelKey]);
    updateSplitComboLabels(initial[suspensionKey], initial[wheelKey]);

    susFieldset.addEventListener('change', function () {
      refreshWheelOptions(null);
      tryResolveFromCustom();
    });
    wheelFieldset.addEventListener('change', tryResolveFromCustom);

    cfgEl.setAttribute('data-stinner-split-combo-initialized', 'true');
  }

  function boot() {
    document.querySelectorAll('[data-stinner-split-combo-config]').forEach(initFromConfigEl);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(boot, 50);
    });
  } else {
    setTimeout(boot, 50);
  }
  document.addEventListener('page:loaded', boot);
})();
